import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { useAuth } from 'auth/AuthProvider';
import { queryKeys } from 'core/api/queryKeys';
import { captureRevision, setExpectedRevision } from 'core/api/revisionStore';
import { notifySessionExpired } from 'core/api/sessionEvents';
import { ApiError } from 'core/errors/ApiError';
import { humanizeApiError } from 'core/errors/mapFieldErrors';
import { useI18n } from 'core/i18n/I18nProvider';
import { paths } from 'core/routing/paths';
import { assessmentApi } from 'features/assessment-dashboard/assessment.api';
import {
  CLIENT_MAX_UPLOAD_BYTES,
  WORKBOOK_ACCEPT,
  filterWorkbookErrors,
  groupErrorsBySheet,
  hasDestructiveChanges,
  isUnsupportedWorkbookSchema,
  normalizePreview,
  previewFromApiError,
  saveBlobFile,
  summaryRowsFor,
  validateWorkbookFile,
} from 'features/workbooks/workbook';
import { Button } from 'shared/components/Button';
import { Badge } from 'shared/components/Feedback';
import { SwitchField, TextField } from 'shared/forms/Fields';
import { formatFileSize } from 'shared/utilities/format';

const STEPS = [
  'workbook.steps.select',
  'workbook.steps.preview',
  'workbook.steps.confirm',
  'workbook.steps.completed',
];

function getFocusable(root) {
  if (!root) {
    return [];
  }
  return Array.from(root.querySelectorAll('button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'))
    .filter((node) => !node.hasAttribute('disabled') && node.getAttribute('aria-hidden') !== 'true');
}

function warningTone(warning) {
  const haystack = `${warning.code || ''} ${warning.message || ''}`.toLowerCase();
  if (warning.destructive || /delete|archiv|detach/.test(haystack)) {
    return 'danger';
  }
  return 'warning';
}

export function ImportDialog({
  open,
  onClose,
  restoreFocusRef,
  kind,
  versionId,
  explanations = [],
  guidanceTitleKey = 'workbook.importGuidanceTitle',
  previewRequest,
  commitRequest,
  downloadLatest,
  onSuccess,
  successAction,
}) {
  const { t, locale, dir } = useI18n();
  const { logout } = useAuth();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const dialogRef = useRef(null);
  const closeRef = useRef(null);
  const fileInputRef = useRef(null);
  const errorSummaryRef = useRef(null);
  const inflightRef = useRef(false);
  const [step, setStep] = useState(0);
  const [file, setFile] = useState(null);
  const [fileError, setFileError] = useState('');
  const [dragActive, setDragActive] = useState(false);
  const [busy, setBusy] = useState(null);
  const [uploadProgress, setUploadProgress] = useState(null);
  const [preview, setPreview] = useState(null);
  const [errorFilter, setErrorFilter] = useState('');
  const [confirmed, setConfirmed] = useState(false);
  const [stale, setStale] = useState(false);
  const [commitUnknown, setCommitUnknown] = useState(false);
  const [forbidden, setForbidden] = useState(false);
  const [statusMessage, setStatusMessage] = useState('');
  const [result, setResult] = useState(null);
  const [topError, setTopError] = useState('');

  const requestInFlight = Boolean(busy);
  const destructive = hasDestructiveChanges(preview);
  const schemaUnsupported = isUnsupportedWorkbookSchema(preview);
  const filteredErrors = useMemo(
    () => filterWorkbookErrors(preview?.errors || [], errorFilter),
    [errorFilter, preview],
  );
  const errorGroups = useMemo(() => groupErrorsBySheet(filteredErrors), [filteredErrors]);
  const summaryRows = useMemo(
    () => summaryRowsFor(kind, result?.applied || preview?.summary),
    [kind, preview, result],
  );

  const resetPreviewState = useCallback(() => {
    setPreview(null);
    setConfirmed(false);
    setErrorFilter('');
    if (step > 1 && step < 3) {
      setStep(1);
    }
  }, [step]);

  const resetAll = useCallback(() => {
    setStep(0);
    setFile(null);
    setFileError('');
    setPreview(null);
    setConfirmed(false);
    setStale(false);
    setCommitUnknown(false);
    setForbidden(false);
    setResult(null);
    setTopError('');
    setUploadProgress(null);
    setBusy(null);
    inflightRef.current = false;
  }, []);

  const closeDialog = useCallback(() => {
    if (inflightRef.current) {
      return;
    }
    resetAll();
    onClose();
    const previous = restoreFocusRef?.current;
    if (previous && typeof previous.focus === 'function') {
      previous.focus();
    }
  }, [onClose, resetAll, restoreFocusRef]);

  useEffect(() => {
    if (!open) {
      return undefined;
    }
    closeRef.current?.focus();
    const onKeyDown = (event) => {
      if (event.key === 'Escape') {
        if (!inflightRef.current) {
          event.preventDefault();
          closeDialog();
        }
        return;
      }
      if (event.key !== 'Tab') {
        return;
      }
      const focusable = getFocusable(dialogRef.current);
      if (focusable.length === 0) {
        event.preventDefault();
        return;
      }
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [closeDialog, open]);

  const applyFile = (nextFile) => {
    if (!nextFile) {
      return;
    }
    const check = validateWorkbookFile(nextFile, { maxBytes: CLIENT_MAX_UPLOAD_BYTES });
    if (!check.ok) {
      setFile(null);
      setFileError(check.code === 'oversized' ? t('workbook.errors.oversized') : t('workbook.errors.extension'));
      return;
    }
    setFile(nextFile);
    setFileError('');
    setPreview(null);
    setConfirmed(false);
    setStale(false);
    setCommitUnknown(false);
    setTopError('');
    setStep(0);
  };

  const handleForbidden = async () => {
    setForbidden(true);
    try {
      await logout();
    } finally {
      navigate(paths.login, { replace: true });
    }
  };

  const progressConfig = {
    onUploadProgress: (event) => {
      if (event?.total) {
        setUploadProgress(Math.round((event.loaded / event.total) * 100));
      } else {
        setUploadProgress(null);
      }
    },
  };

  const runPreview = async () => {
    if (!file || inflightRef.current || commitUnknown) {
      return;
    }
    inflightRef.current = true;
    setBusy('preview');
    setStatusMessage(t('workbook.status.validating'));
    setTopError('');
    setStale(false);
    setPreview(null);
    setStep(1);
    try {
      const data = await previewRequest(file, progressConfig);
      const next = normalizePreview(data);
      setPreview(next);
      if (next.errors.length > 0 || !next.canCommit) {
        setTopError(next.message || t('workbook.errors.validationTitle'));
        requestAnimationFrame(() => errorSummaryRef.current?.focus());
      } else {
        setStep(2);
      }
    } catch (error) {
      if (error instanceof ApiError && error.isUnauthorized()) {
        notifySessionExpired();
        return;
      }
      if (error instanceof ApiError && error.isForbidden()) {
        setForbidden(true);
        setTopError(t('errors.forbidden'));
        return;
      }
      if (error instanceof ApiError && error.isGenerationInProgress()) {
        setPreview(null);
        setTopError(t('results.generationLock'));
        return;
      }
      if (error instanceof ApiError && error.isConflict()) {
        setStale(true);
        setPreview(null);
        setTopError(t('workbook.errors.staleRevision'));
        setStep(0);
        requestAnimationFrame(() => errorSummaryRef.current?.focus());
        return;
      }
      if (error instanceof ApiError && (error.isValidation() || error.isPayloadTooLarge() || error.isUnsupportedMedia())) {
        const next = previewFromApiError(error);
        setPreview(next);
        setTopError(humanizeApiError(error, t));
        requestAnimationFrame(() => errorSummaryRef.current?.focus());
        return;
      }
      setPreview(null);
      if (error instanceof ApiError && error.isNetworkError) {
        setTopError(t('workbook.errors.networkPreview'));
        return;
      }
      setTopError(humanizeApiError(error, t) || error?.message || t('errors.unexpected'));
    } finally {
      inflightRef.current = false;
      setBusy(null);
      setUploadProgress(null);
      setStatusMessage('');
    }
  };

  const runCommit = async () => {
    if (!file || !preview?.canCommit || inflightRef.current || stale || commitUnknown) {
      return;
    }
    if ((preview.requiresConfirmation || destructive) && !confirmed) {
      return;
    }
    inflightRef.current = true;
    setBusy('commit');
    setStatusMessage(t('workbook.status.committing'));
    setTopError('');
    try {
      const data = await commitRequest(file, {
        fileSha256: preview.fileSha256,
        commitToken: preview.commitToken || preview.previewToken,
        expectedRevision: preview.currentRevision ?? preview.baseRevision,
        ...(kind === 'top-three-profiles'
          ? {}
          : { confirmDestructive: confirmed || preview.requiresConfirmation || destructive }),
      }, progressConfig);
      const completed = normalizePreview(data);
      if (completed.revision) {
        setExpectedRevision(completed.revision);
      }
      captureRevision(data);
      if (typeof onSuccess === 'function') {
        await onSuccess(completed);
      }
      setResult(completed);
      setStep(3);
    } catch (error) {
      if (error instanceof ApiError && error.isUnauthorized()) {
        notifySessionExpired();
        return;
      }
      if (error instanceof ApiError && error.isForbidden()) {
        setForbidden(true);
        setTopError(t('errors.forbidden'));
        return;
      }
      if (error instanceof ApiError && error.isGenerationInProgress()) {
        setTopError(t('results.generationLock'));
        return;
      }
      if (error instanceof ApiError && error.isConflict()) {
        setStale(true);
        setPreview((current) => (current ? { ...current, canCommit: false } : previewFromApiError(error)));
        setTopError(t('workbook.errors.staleRevision'));
        setStep(0);
        return;
      }
      if (error instanceof ApiError && error.isNetworkError) {
        setCommitUnknown(true);
        setPreview((current) => (current ? { ...current, canCommit: false } : current));
        setTopError(t('workbook.errors.commitUnknown'));
        try {
          const overview = await assessmentApi.overview();
          captureRevision(overview);
          await queryClient.invalidateQueries({ queryKey: queryKeys.overview });
        } catch {
          // Refresh is best-effort before another commit is allowed.
        }
        return;
      }
      if (error instanceof ApiError && error.isValidation()) {
        const next = previewFromApiError(error);
        setPreview(next);
        setStep(1);
        setTopError(humanizeApiError(error, t));
        requestAnimationFrame(() => errorSummaryRef.current?.focus());
        return;
      }
      setTopError(humanizeApiError(error, t));
    } finally {
      inflightRef.current = false;
      setBusy(null);
      setUploadProgress(null);
      setStatusMessage('');
    }
  };

  const refreshConfiguration = async () => {
    if (inflightRef.current) {
      return;
    }
    inflightRef.current = true;
    setBusy('refresh');
    try {
      const overview = await assessmentApi.overview();
      captureRevision(overview);
      await queryClient.invalidateQueries({ queryKey: queryKeys.overview });
      if (kind === 'top-three-profiles' && versionId) {
        await queryClient.invalidateQueries({ queryKey: queryKeys.version(versionId) });
      }
      resetPreviewState();
      setStep(0);
      setStale(false);
      setCommitUnknown(false);
      setTopError(t('workbook.repreviewRequired'));
    } catch (error) {
      setTopError(humanizeApiError(error, t));
    } finally {
      inflightRef.current = false;
      setBusy(null);
    }
  };

  const downloadLatestExport = async () => {
    if (!downloadLatest || inflightRef.current) {
      return;
    }
    inflightRef.current = true;
    setBusy('download');
    try {
      const downloaded = await downloadLatest();
      saveBlobFile(downloaded.blob, downloaded.filename);
    } catch (error) {
      if (error instanceof ApiError && error.isForbidden()) {
        setForbidden(true);
        return;
      }
      setTopError(humanizeApiError(error, t));
    } finally {
      inflightRef.current = false;
      setBusy(null);
    }
  };

  if (!open) {
    return null;
  }

  const requiresCommitToken = kind === 'top-three-profiles';
  const commitToken = preview?.commitToken || preview?.previewToken;
  const canCommit = Boolean(
    file
    && preview?.canCommit
    && preview.errors.length === 0
    && !stale
    && !commitUnknown
    && !schemaUnsupported
    && (!requiresCommitToken || Boolean(commitToken))
    && (!(destructive || preview.requiresConfirmation) || confirmed),
  );

  return (
    <div
      className="modal-backdrop"
      data-testid="workbook-import-backdrop"
      role="presentation"
      onClick={() => {
        if (!requestInFlight) {
          closeDialog();
        }
      }}
    >
      <div
        ref={dialogRef}
        className="modal modal-wide workbook-import-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="workbook-import-title"
        dir={dir}
        onClick={(event) => event.stopPropagation()}
      >
        <div className="modal-header">
          <div className="toolbar wrap modal-header-row">
            <h2 id="workbook-import-title">{t(kind === 'top-three-profiles' ? 'workbook.importResults' : 'workbook.importQuestionnaire')}</h2>
            <Button ref={closeRef} tone="ghost" onClick={closeDialog} disabled={requestInFlight}>{t('actions.close')}</Button>
          </div>
          <ol className="wizard-steps" aria-label={t('workbook.steps.label')}>
            {STEPS.map((key, index) => (
              <li key={key} className={index === step ? 'is-current' : index < step ? 'is-done' : ''} aria-current={index === step ? 'step' : undefined}>
                {t(key)}
              </li>
            ))}
          </ol>
        </div>
        <div className="modal-body">
        <div className="sr-only" aria-live="polite">{statusMessage}</div>
        {explanations.length > 0 ? (
          <details className="workbook-import-guidance">
            <summary>{t(guidanceTitleKey)}</summary>
            <div className="workbook-import-guidance-body">
              {explanations.map((key) => (
                <p key={key}>{t(key)}</p>
              ))}
            </div>
          </details>
        ) : null}
        {topError ? (
          <p ref={errorSummaryRef} id="workbook-error-summary" className="form-banner" role="alert" tabIndex={-1}>
            {topError}
          </p>
        ) : null}
        {forbidden ? (
          <div className="toolbar wrap">
            <Button tone="ghost" onClick={handleForbidden}>{t('actions.logout')}</Button>
            <Button onClick={handleForbidden}>{t('forbidden.switchAdmin')}</Button>
          </div>
        ) : null}
        {stale || commitUnknown ? (
          <div className="warning-banner">
            <p>{stale ? t('workbook.errors.staleInstructions') : t('workbook.errors.commitUnknown')}</p>
            <div className="toolbar wrap">
              <Button tone="secondary" onClick={refreshConfiguration} loading={busy === 'refresh'} disabled={requestInFlight}>
                {t('workbook.refreshConfiguration')}
              </Button>
              {downloadLatest ? (
                <Button tone="secondary" onClick={downloadLatestExport} loading={busy === 'download'} disabled={requestInFlight}>
                  {t('workbook.downloadLatestExport')}
                </Button>
              ) : null}
            </div>
          </div>
        ) : null}

        {step < 3 ? (
          <div className="stack-form">
            {step === 0 ? (
              <>
                <label className="field" htmlFor="workbook-file-input">
                  <span className="field-label">{t('workbook.selectFile')}</span>
                </label>
                <div
                  className={dragActive ? 'dropzone is-active' : 'dropzone'}
                  data-testid="workbook-dropzone"
                  onDragOver={(event) => {
                    event.preventDefault();
                    setDragActive(true);
                  }}
                  onDragLeave={() => setDragActive(false)}
                  onDrop={(event) => {
                    event.preventDefault();
                    setDragActive(false);
                    applyFile(event.dataTransfer?.files?.[0]);
                  }}
                >
                  <p>{t('workbook.dropzone')}</p>
                  <input
                    ref={fileInputRef}
                    id="workbook-file-input"
                    className="input"
                    type="file"
                    accept={WORKBOOK_ACCEPT}
                    onChange={(event) => applyFile(event.target.files?.[0])}
                  />
                </div>
                <p className="muted">{t('workbook.fileRestrictions')}</p>
                {fileError ? <p className="field-error" role="alert">{fileError}</p> : null}
              </>
            ) : null}
            {file ? (
              <div className="file-meta-card">
                <div className="file-meta-copy">
                  <strong className="file-meta-name">{file.name}</strong>
                  <span className="muted">{formatFileSize(file.size, locale)}</span>
                </div>
                <Button
                  tone="ghost"
                  onClick={() => {
                    setFile(null);
                    setFileError('');
                    setPreview(null);
                    setStep(0);
                    if (fileInputRef.current) {
                      fileInputRef.current.value = '';
                    }
                  }}
                  disabled={requestInFlight}
                >
                  {t('workbook.removeFile')}
                </Button>
              </div>
            ) : null}
            {busy === 'preview' || busy === 'commit' ? (
              <div className="progress-bar" role="status">
                {uploadProgress != null
                  ? t('workbook.status.uploadProgress', { percent: uploadProgress })
                  : t('workbook.status.working')}
              </div>
            ) : null}
            {preview && step >= 1 ? (
              <PreviewSummary
                t={t}
                preview={preview}
                summaryRows={summaryRows}
                errorGroups={errorGroups}
                errorFilter={errorFilter}
                onFilterChange={setErrorFilter}
                dir={dir}
                schemaUnsupported={schemaUnsupported}
                kind={kind}
              />
            ) : null}
            {preview && (destructive || preview?.requiresConfirmation) ? (
              <div className="warning-banner">
                <p>{kind === 'top-three-profiles' ? t('workbook.fullSyncNote') : t('workbook.destructiveNote')}</p>
                <SwitchField
                  id="workbook-destructive-confirm"
                  label={t('workbook.destructiveConfirm')}
                  checked={confirmed}
                  onChange={setConfirmed}
                  disabled={requestInFlight}
                />
              </div>
            ) : null}
          </div>
        ) : (
          <div className="stack-form">
            <p className="success-banner" role="status">{t('workbook.success')}</p>
            <p>{t('workbook.newRevision')}: {result?.revision ?? '—'}</p>
            <SummaryTable t={t} rows={summaryRows} />
            <div className="toolbar wrap">
              <Button onClick={closeDialog}>{t('actions.close')}</Button>
              {successAction ? (
                <Button
                  tone="secondary"
                  onClick={() => {
                    closeDialog();
                    navigate(successAction.path);
                  }}
                >
                  {t(successAction.labelKey)}
                </Button>
              ) : null}
            </div>
          </div>
        )}

        </div>

        {step < 3 ? (
          <div className="modal-footer">
            <div className="modal-actions">
              <Button tone="ghost" onClick={closeDialog} disabled={requestInFlight}>{t('actions.cancel')}</Button>
              {step > 0 ? (
                <Button tone="secondary" onClick={() => setStep(Math.max(0, step - 1))} disabled={requestInFlight}>{t('actions.back')}</Button>
              ) : null}
              <Button onClick={runPreview} loading={busy === 'preview'} disabled={!file || requestInFlight || Boolean(fileError)}>
                {t('workbook.validatePreview')}
              </Button>
              {step === 1 && preview?.canCommit ? (
                <Button onClick={() => setStep(2)} disabled={requestInFlight}>{t('actions.next')}</Button>
              ) : null}
              <Button
                data-testid="workbook-commit"
                onClick={runCommit}
                loading={busy === 'commit'}
                disabled={!canCommit || requestInFlight}
              >
                {t('workbook.commit')}
              </Button>
            </div>
          </div>
        ) : null}
      </div>
    </div>
  );
}

function PreviewSummary({ t, preview, summaryRows, errorGroups, errorFilter, onFilterChange, dir, schemaUnsupported, kind }) {
  return (
    <div className="workbook-preview">
      {schemaUnsupported ? (
        <p className="warning-banner" role="alert" data-testid="workbook-schema-unsupported">
          {t('workbook.errors.unsupportedSchema', {
            fileVersion: preview.schemaVersion || '—',
            supportedVersion: preview.supportedSchemaVersion || '—',
          })}
        </p>
      ) : null}
      <dl className="meta-grid">
        <div><dt>{t('workbook.workbookType')}</dt><dd>{preview.workbookType || '—'}</dd></div>
        <div><dt>{t('workbook.schemaVersion')}</dt><dd data-testid="workbook-schema-version">{preview.schemaVersion || '—'}</dd></div>
        <div><dt>{t('workbook.supportedSchemaVersion')}</dt><dd data-testid="workbook-supported-schema-version">{preview.supportedSchemaVersion || '—'}</dd></div>
        <div><dt>{t('workbook.baseRevision')}</dt><dd>{preview.baseRevision ?? '—'}</dd></div>
        <div><dt>{t('workbook.currentRevision')}</dt><dd>{preview.currentRevision ?? '—'}</dd></div>
        {kind === 'top-three-profiles' ? (
          <>
            <div><dt>{t('workbook.summary.profilesExpected')}</dt><dd data-testid="preview-expected-profiles">{preview.expectedProfiles || 120}</dd></div>
            <div><dt>{t('workbook.summary.profilesFound')}</dt><dd data-testid="preview-found-profiles">{preview.foundProfiles}</dd></div>
            <div><dt>{t('workbook.summary.profilesMissing')}</dt><dd>{Array.isArray(preview.missingProfiles) ? preview.missingProfiles.length : preview.missingProfiles || 0}</dd></div>
            <div><dt>{t('workbook.summary.invalidKeys')}</dt><dd>{Array.isArray(preview.invalidKeys) ? preview.invalidKeys.length : 0}</dd></div>
            <div><dt>{t('workbook.summary.duplicateKeys')}</dt><dd>{Array.isArray(preview.duplicateKeys) ? preview.duplicateKeys.length : 0}</dd></div>
            <div><dt>{t('workbook.summary.profilesChanged')}</dt><dd data-testid="preview-profiles-changed">{preview.profilesChanged || 0}</dd></div>
            <div><dt>{t('workbook.summary.relationshipsRemoved')}</dt><dd data-testid="preview-relationships-removed">{preview.relationshipsRemoved || 0}</dd></div>
          </>
        ) : null}
        <div>
          <dt>{t('workbook.canCommit')}</dt>
          <dd><Badge tone={preview.canCommit ? 'success' : 'warning'}>{preview.canCommit ? t('workbook.yes') : t('workbook.no')}</Badge></dd>
        </div>
        <div className="meta-span-full">
          <dt>{t('workbook.fileHash')}</dt>
          <dd className="meta-hash" title={preview.fileSha256}>{preview.fileSha256 || '—'}</dd>
        </div>
      </dl>
      <SummaryTable t={t} rows={summaryRows} />
      {kind === 'top-three-profiles' && (preview.missingRequiredByProfile || []).length > 0 ? (
        <section data-testid="preview-missing-required">
          <h3>{t('profiles.missingRequired')}</h3>
          <ul>
            {(preview.missingRequiredByProfile || []).map((row, index) => (
              <li key={row.canonical_key || row.profile_id || index}>
                {row.canonical_key || row.profile_id || t('profiles.incomplete')}: {Array.isArray(row.missing) ? row.missing.join(', ') : (row.message || String(row))}
              </li>
            ))}
          </ul>
        </section>
      ) : null}
      {(preview.warnings || []).length > 0 ? (
        <section>
          <h3>{t('workbook.warnings')}</h3>
          <ul className="warning-list">
            {preview.warnings.map((warning, index) => (
              <li key={`${warning.code}-${index}`}>
                <Badge tone={warningTone(warning)}>{warning.code || t('workbook.warning')}</Badge>
                <span>{warning.message}</span>
              </li>
            ))}
          </ul>
        </section>
      ) : null}
      {(preview.errors || []).length > 0 ? (
        <section>
          <h3>{t('workbook.errors.tableTitle')}</h3>
          <TextField
            id="workbook-error-filter"
            label={t('workbook.filterErrors')}
            value={errorFilter}
            onChange={onFilterChange}
          />
          <div className="table-wrap" dir={dir}>
            <table className="data-table workbook-error-table" data-testid="workbook-error-table">
              <thead>
                <tr>
                  <th>{t('workbook.errors.sheet')}</th>
                  <th>{t('workbook.errors.cell')}</th>
                  <th>{t('workbook.errors.message')}</th>
                  <th>{t('workbook.errors.value')}</th>
                </tr>
              </thead>
              <tbody>
                {errorGroups.flatMap((group) => group.rows.map((error) => (
                  <tr key={`${error.order}-${error.cell}`}>
                    <td>{error.sheet || '—'}</td>
                    <td>{error.cell || [error.column, error.row].filter(Boolean).join('') || '—'}</td>
                    <td>{error.message}</td>
                    <td>{error.value === '' || error.value == null ? '—' : String(error.value)}</td>
                  </tr>
                )))}
              </tbody>
            </table>
          </div>
        </section>
      ) : null}
    </div>
  );
}

function SummaryTable({ t, rows }) {
  return (
    <div className="workbook-summary-grid" role="table" aria-label={t('workbook.changeSummary')}>
      {rows.map((row) => (
        <div
          key={row.key}
          className={`workbook-summary-row${row.value === 0 ? ' is-zero' : ''}`}
          role="row"
          data-testid={`workbook-count-${row.key}`}
        >
          <span className="workbook-summary-label" role="rowheader">{t(row.key)}</span>
          <span className="workbook-summary-value" role="cell">{row.value}</span>
        </div>
      ))}
    </div>
  );
}
